import { useState, useEffect, useMemo } from 'react';
import { HoldingsSubscriptionSeat, HoldingsCompany, HoldingsPerson, HoldingsCompanyPerson } from '../../types';
import { Button, Input, Select, Textarea } from "@repo/o37-ui";
import { X, Building, User } from "lucide-react";

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

interface PersonWithCompanies extends HoldingsPerson {
  companyAssignments?: HoldingsCompanyPerson[];
}

interface SeatAssignmentFormProps {
  subscriptionId: string;
  maxSeats: number;
  seat?: HoldingsSubscriptionSeat | null;
  onClose: () => void;
  onSaved: () => void;
}

export default function SeatAssignmentForm({
  subscriptionId,
  maxSeats,
  seat,
  onClose,
  onSaved
}: SeatAssignmentFormProps) {
  const isEditing = !!seat;

  // Assignment type: 'company' = company only, 'person' = person only, 'both' = person at specific company
  const [assigneeType, setAssigneeType] = useState<'company' | 'person' | 'both'>(
    seat?.companyId && seat?.personId ? 'both' :
    seat?.companyId ? 'company' : 'person'
  );
  const [companyId, setCompanyId] = useState(seat?.companyId || '');
  const [personId, setPersonId] = useState(seat?.personId || '');
  const [seatsAssigned, setSeatsAssigned] = useState(seat?.seatsAssigned?.toString() || '1');
  const [notes, setNotes] = useState(seat?.notes || '');
  const [filterByCompany, setFilterByCompany] = useState('');

  const [companies, setCompanies] = useState<HoldingsCompany[]>([]);
  const [people, setPeople] = useState<PersonWithCompanies[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const userId = localStorage.getItem('app_user_id') || '';
  const userRole = localStorage.getItem('app_role') || 'user';

  useEffect(() => {
    loadCompanies();
    loadPeople();
  }, []);

  const loadCompanies = async () => {
    try {
      const response = await fetch(`${API_URL}/holdings/companies`, {
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });
      if (response.ok) {
        const data = await response.json();
        setCompanies(data.filter((c: any) => c.status === 'active').map((c: any) => ({
          id: c.id,
          name: c.name,
          status: c.status
        })));
      }
    } catch (e) {
      console.error('Failed to load companies:', e);
    }
  };

  const loadPeople = async () => {
    try {
      const response = await fetch(`${API_URL}/holdings/people`, {
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });
      if (response.ok) {
        const data = await response.json();
        setPeople(data.filter((p: any) => p.is_active).map((p: any) => ({
          id: p.id,
          name: p.name,
          email: p.email,
          isActive: p.is_active,
          companyAssignments: p.holdings_company_people?.map((cp: any) => ({
            id: cp.id,
            companyId: cp.company?.id,
            personId: p.id,
            roleAtCompany: cp.role_at_company,
            isPrimaryContact: cp.is_primary_contact,
            company: cp.company ? {
              id: cp.company.id,
              name: cp.company.name
            } : undefined
          })) || []
        })));
      }
    } catch (e) {
      console.error('Failed to load people:', e);
    }
  };

  // Filter people by selected company if filter is active
  const filteredPeople = useMemo(() => {
    if (!filterByCompany) return people;
    return people.filter(p =>
      p.companyAssignments?.some(ca => ca.companyId === filterByCompany)
    );
  }, [people, filterByCompany]);

  // Get company name for display
  const getPersonCompanyNames = (person: PersonWithCompanies): string => {
    if (!person.companyAssignments || person.companyAssignments.length === 0) {
      return '';
    }
    return person.companyAssignments
      .map(ca => ca.company?.name)
      .filter(Boolean)
      .join(', ');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    let selectedCompanyId = '';
    let selectedPersonId = '';

    if (assigneeType === 'company') {
      selectedCompanyId = companyId;
    } else if (assigneeType === 'person') {
      selectedPersonId = personId;
    } else if (assigneeType === 'both') {
      selectedCompanyId = companyId;
      selectedPersonId = personId;
    }

    if (assigneeType === 'company' && !selectedCompanyId) {
      setError('Please select a company');
      return;
    }
    if (assigneeType === 'person' && !selectedPersonId) {
      setError('Please select a person');
      return;
    }
    if (assigneeType === 'both' && (!selectedCompanyId || !selectedPersonId)) {
      setError('Please select both a company and a person');
      return;
    }

    const seats = parseInt(seatsAssigned);
    if (!seats || seats < 1) {
      setError('Seats must be at least 1');
      return;
    }

    if (seats > maxSeats) {
      setError(`Only ${maxSeats} seat${maxSeats !== 1 ? 's' : ''} available`);
      return;
    }

    setSaving(true);
    setError('');

    try {
      const payload = {
        companyId: selectedCompanyId || null,
        personId: selectedPersonId || null,
        seatsAssigned: seats,
        notes: notes.trim() || null
      };

      const url = isEditing
        ? `${API_URL}/holdings/subscriptions/${subscriptionId}/seats/${seat.id}`
        : `${API_URL}/holdings/subscriptions/${subscriptionId}/seats`;

      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
          'x-user-role': userRole
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        onSaved();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to save seat assignment');
      }
    } catch (e) {
      setError('Failed to save seat assignment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-100">
          <h2 className="text-xl font-semibold">
            {isEditing ? 'Edit Seat Assignment' : 'Assign Seats'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded-lg">
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg border border-red-200">
              {error}
            </div>
          )}

          {/* Assignee Type */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Assign To
            </label>
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="assigneeType"
                  checked={assigneeType === 'company'}
                  onChange={() => {
                    setAssigneeType('company');
                    setPersonId('');
                    setFilterByCompany('');
                  }}
                  className="text-blue-600"
                />
                <span className="text-sm flex items-center gap-1">
                  <Building className="w-3.5 h-3.5" />
                  Company
                </span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="assigneeType"
                  checked={assigneeType === 'person'}
                  onChange={() => {
                    setAssigneeType('person');
                    setCompanyId('');
                    setFilterByCompany('');
                  }}
                  className="text-blue-600"
                />
                <span className="text-sm flex items-center gap-1">
                  <User className="w-3.5 h-3.5" />
                  Person
                </span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="assigneeType"
                  checked={assigneeType === 'both'}
                  onChange={() => {
                    setAssigneeType('both');
                  }}
                  className="text-blue-600"
                />
                <span className="text-sm flex items-center gap-1">
                  <User className="w-3.5 h-3.5" />
                  <span>+</span>
                  <Building className="w-3.5 h-3.5" />
                  Person at Company
                </span>
              </label>
            </div>
          </div>

          {/* Company Select - shown for 'company' and 'both' */}
          {(assigneeType === 'company' || assigneeType === 'both') && (
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Company *
              </label>
              <Select
                value={companyId}
                onChange={(e) => {
                  setCompanyId(e.target.value);
                  // When in 'both' mode, filter people by selected company
                  if (assigneeType === 'both') {
                    setFilterByCompany(e.target.value);
                    setPersonId(''); // Reset person selection when company changes
                  }
                }}
                required
              >
                <option value="">Select company...</option>
                {companies.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </Select>
            </div>
          )}

          {/* Person Select - shown for 'person' and 'both' */}
          {(assigneeType === 'person' || assigneeType === 'both') && (
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Person *
              </label>
              {/* Optional company filter when in 'person' mode */}
              {assigneeType === 'person' && (
                <div className="mb-2">
                  <Select
                    value={filterByCompany}
                    onChange={(e) => {
                      setFilterByCompany(e.target.value);
                      setPersonId(''); // Reset person selection when filter changes
                    }}
                    className="text-sm"
                  >
                    <option value="">All companies</option>
                    {companies.map(c => (
                      <option key={c.id} value={c.id}>Filter by: {c.name}</option>
                    ))}
                  </Select>
                </div>
              )}
              <Select
                value={personId}
                onChange={(e) => setPersonId(e.target.value)}
                required
                disabled={assigneeType === 'both' && !companyId}
              >
                <option value="">
                  {assigneeType === 'both' && !companyId
                    ? 'Select a company first...'
                    : 'Select person...'}
                </option>
                {filteredPeople.map(p => {
                  const companyNames = getPersonCompanyNames(p);
                  return (
                    <option key={p.id} value={p.id}>
                      {p.name}{companyNames ? ` (${companyNames})` : ''}
                    </option>
                  );
                })}
              </Select>
              {assigneeType === 'both' && companyId && filteredPeople.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">
                  No people assigned to this company yet
                </p>
              )}
            </div>
          )}

          {/* Seats */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Seats to Assign *
            </label>
            <Input
              value={seatsAssigned}
              onChange={(e) => setSeatsAssigned(e.target.value)}
              type="number"
              min="1"
              max={maxSeats}
              required
            />
            <p className="text-xs text-neutral-500 mt-1">
              {maxSeats} seat{maxSeats !== 1 ? 's' : ''} available
            </p>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Notes
            </label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes about this assignment..."
              rows={2}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-neutral-100">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving} text="Cancel" />
            <Button type="submit" disabled={saving} text={saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Assign Seats'} />
          </div>
        </form>
      </div>
    </div>
  );
}
